import { getClientId } from "@/lib/client-id";
import { buildApiUrl } from "@/lib/api";

interface ErrorPayload {
  message: string;
  stack?: string;
  path: string;
  source: string;
}

function getMonitoringEndpoint(): string | null {
  const raw = import.meta.env.VITE_CLIENT_ERROR_ENDPOINT;
  if (typeof raw === "string") {
    const value = raw.trim();
    if (value.length > 0) return value;
  }

  return buildApiUrl("/api/client-error");
}

async function sendError(payload: ErrorPayload): Promise<void> {
  const endpoint = getMonitoringEndpoint();
  if (!endpoint) return;

  const body = JSON.stringify({
    ...payload,
    clientId: getClientId(),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  try {
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Client-Id": getClientId(),
      },
      body,
      keepalive: true,
    });
  } catch {
    // Best-effort only.
  }
}

export function reportClientError(error: Error, source = "error-boundary"): void {
  void sendError({
    message: error.message,
    stack: error.stack,
    path: window.location.pathname,
    source,
  });
}

export function initMonitoring(): void {
  window.addEventListener("error", (event) => {
    const maybeError = event.error instanceof Error ? event.error : new Error(event.message);
    reportClientError(maybeError, "window-error");
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === "string" ? reason : "Unhandled promise rejection");
    reportClientError(error, "unhandled-rejection");
  });
}
